import {
    JobStatusType,
    JobStepStatusType,
    type Job,
    type JobStep,
} from '@lightdash/common';
import {
    Alert,
    Box,
    Group,
    Loader,
    Stack,
    Text,
    Title,
    Drawer,
    Button,
    type DefaultMantineColor,
} from '@mantine/core';
import { useInterval } from '@mantine/hooks';
import {
    IconAlertTriangle,
    IconAlertTriangleFilled,
    IconCircleCheckFilled,
    IconDownload,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';
import { useEffect, useState, type FC } from 'react';
import {
    jobStatusLabel,
    jobStepStatusLabel,
    runningStepsInfo,
} from '../../hooks/useRefreshServer';
import useActiveJob from '../../providers/ActiveJob/useActiveJob';
import { CopyActionIcon } from '../common/CopyActionIcon';
import MantineIcon from '../common/MantineIcon';
import { formatFailedJobLog } from './failedJobLog';
import classes from './JobDetailsDrawer.module.css';
import ProjectCompileLog from './ProjectCompileLog';

dayjs.extend(duration);
dayjs.extend(utc);

const statusInfo = (status: string): DefaultMantineColor => {
    switch (status) {
        case 'DONE':
            return 'green';
        case 'ERROR':
            return 'red';
        case 'SKIPPED':
            return 'gray';
        default:
            return 'gray';
    }
};

const durationSince = (
    startTime: Date,
    endTime: Date | undefined = undefined,
) => {
    return dayjs
        .utc(
            dayjs
                .duration(dayjs(endTime).diff(dayjs(startTime)))
                .asMilliseconds(),
        )
        .format('HH:mm:ss');
};

const jobStepDuration = (jobStep: JobStep): string | null => {
    switch (jobStep.stepStatus) {
        case JobStepStatusType.ERROR:
        case JobStepStatusType.DONE:
            return durationSince(
                jobStep.startedAt || jobStep.updatedAt,
                jobStep.updatedAt,
            );
        case JobStepStatusType.RUNNING:
            return durationSince(jobStep.startedAt || jobStep.updatedAt);
        default:
            return null;
    }
};

type DrawerIconProps = { job: Job };
const DrawerIcon: FC<DrawerIconProps> = ({ job }) => {
    switch (job.jobStatus) {
        case JobStatusType.ERROR:
            return <MantineIcon icon={IconAlertTriangle} size="xl" />;
        case JobStatusType.DONE:
            return <MantineIcon icon={IconCircleCheckFilled} size="xl" />;
        case JobStatusType.RUNNING:
            return <Loader color="gray" size="sm" />;
        case JobStatusType.STARTED:
            return null;
        default:
            throw new Error('Unknown job status');
    }
};

type StepIconProps = { step: JobStep };
const StepIcon: FC<StepIconProps> = ({ step }) => {
    switch (step.stepStatus) {
        case JobStepStatusType.ERROR:
            return <MantineIcon icon={IconAlertTriangleFilled} />;
        case JobStepStatusType.DONE:
            return <MantineIcon icon={IconCircleCheckFilled} />;
        case JobStepStatusType.RUNNING:
            return <Loader color="gray" size={16} />;
        case JobStepStatusType.SKIPPED:
        case JobStepStatusType.PENDING:
            return null;
        default:
            throw new Error('Unknown job step status');
    }
};

const JobDetailsDrawer: FC = () => {
    const { isJobsDrawerOpen, setIsJobsDrawerOpen, activeJob } = useActiveJob();

    // Force re-render every second so elapsed timers update in real-time
    const [, setTick] = useState(0);
    const isRunning = activeJob?.jobStatus === JobStatusType.RUNNING;
    const ticker = useInterval(() => setTick((t) => t + 1), 1000);
    useEffect(() => {
        if (isRunning) {
            ticker.start();
        } else {
            ticker.stop();
        }
        return ticker.stop;
    }, [isRunning]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!activeJob) {
        return null;
    }

    const hasSteps = !!activeJob?.steps.length;
    const isJobDone = activeJob.jobStatus === JobStatusType.DONE;

    return (
        <Drawer
            trapFocus
            closeOnEscape
            closeOnClickOutside
            withOverlay
            withCloseButton
            closeButtonProps={{ 'aria-label': 'Close job details', size: 44 }}
            position="right"
            opened={isJobsDrawerOpen}
            onClose={() => setIsJobsDrawerOpen(false)}
            classNames={{ inner: classes.drawerInner }}
            title={
                <Group wrap="nowrap" align="center" gap="xs">
                    <DrawerIcon job={activeJob} />

                    <Box>
                        <Title order={4}>
                            {jobStatusLabel(
                                activeJob.jobStatus,
                                activeJob.jobType,
                            )}
                        </Title>
                        {hasSteps && (
                            <Text c="dimmed" fz="sm" fw={500}>{`${
                                runningStepsInfo(activeJob.steps)
                                    .completedStepsMessage
                            } steps complete - ${durationSince(
                                activeJob.createdAt,
                            )}`}</Text>
                        )}
                    </Box>
                </Group>
            }
        >
            <Stack p="sm">
                {activeJob.jobStatus === JobStatusType.ERROR && (
                    <Stack gap="xs">
                        <Button
                            variant="default"
                            size="xs"
                            leftSection={<MantineIcon icon={IconDownload} />}
                            onClick={() => {
                                const url = URL.createObjectURL(
                                    new Blob([formatFailedJobLog(activeJob)], {
                                        type: 'text/plain;charset=utf-8',
                                    }),
                                );
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = `compilation-${activeJob.jobUuid}.txt`;
                                link.click();
                                setTimeout(() => URL.revokeObjectURL(url), 0);
                            }}
                        >
                            Download captured logs
                        </Button>
                        <Text size="xs" c="dimmed">
                            Includes saved errors and captured dbt messages.
                            Some command output may be unavailable.
                        </Text>
                    </Stack>
                )}
                {activeJob.steps?.map((step) => (
                    <Alert
                        key={step.jobUuid}
                        color={statusInfo(step.stepStatus)}
                        icon={<StepIcon step={step} />}
                        title={step.stepLabel}
                    >
                        <Stack gap={1} flex={1} miw={0}>
                            <Text fz="xs" fw={500}>
                                {jobStepStatusLabel(step.stepStatus)} (
                                {jobStepDuration(step)})
                            </Text>
                            {step.stepError && (
                                <Stack
                                    mt="xs"
                                    className={classes.stepError}
                                    gap="xs"
                                >
                                    <CopyActionIcon
                                        value={
                                            step.stepError +
                                                step.stepDbtLogs
                                                    ?.filter(
                                                        (log) =>
                                                            log.info.level ===
                                                            'error',
                                                    )
                                                    .map((log) => log.info.msg)
                                                    .join('\n') || ''
                                        }
                                        pos="absolute"
                                        top={6}
                                        right={4}
                                        size="xs"
                                    />
                                    <Stack
                                        gap="xs"
                                        className={classes.stepErrorLogs}
                                    >
                                        <Text
                                            size="xs"
                                            c="red"
                                            className={classes.stepErrorMessage}
                                        >
                                            {step.stepError}
                                        </Text>
                                        {step.stepDbtLogs
                                            ?.filter(
                                                (log) =>
                                                    log.info.level === 'error',
                                            )
                                            .map((log) => (
                                                <Text
                                                    key={log.info.ts}
                                                    size="xs"
                                                    pt="xs"
                                                    className={
                                                        classes.stepErrorLog
                                                    }
                                                >
                                                    {log.info.msg}
                                                </Text>
                                            ))}
                                    </Stack>
                                </Stack>
                            )}
                        </Stack>
                    </Alert>
                ))}
                {isJobDone && activeJob.projectUuid && (
                    <ProjectCompileLog
                        projectUuid={activeJob.projectUuid}
                        jobUuid={activeJob.jobUuid}
                    />
                )}
            </Stack>
        </Drawer>
    );
};

export default JobDetailsDrawer;
